import { CONTENT_WIDTH, bottomMarginItemGlobal } from "../../utils/pdfConstants";
import { getMaaraaikaistarkastusVersiotByKiinteisto, getMaaraaikaistarkastusVersio } from "../../../api/api";

// Same single-composited-canvas-image technique as renderCapexYhteenveto.js
// (every element drawn programmatically onto a canvas, not pdfmake table
// primitives, not a DOM screenshot) — now the standard approach for any PDF
// block with a chart or table. Unlike Capex-yhteenveto, this table's row
// count is unbounded (groups/rows are user-added), so a single image can't
// represent it: an image taller than one page doesn't paginate — pdfmake
// treats it as one atomic block and either fits it whole or pushes it whole
// to the next page. So the table is chunked into several images instead,
// each capped at a safe single-page height, with the column header (and a
// "(jatkuu)" continuation marker on the group title, if a group's rows
// spill across a chunk boundary) repeated at the top of every chunk —
// mirroring a native pdfmake table's headerRows:1 page-repeat behavior.

const STATUS_META = {
  puuttuu: { label: "Tieto puuttuu", color: "#6f42c1" },
  voimassa: { label: "Voimassa", color: "#04aa00" },
  vanhentunut: { label: "Vanhentunut", color: "#ba3b46" },
  ei_sovellu: { label: "Ei sovelleta", color: "#888" },
};

const SOV_LABELS = { "-": "–", kylla: "Kyllä", ei: "Ei" };

const statusText = (row) => {
  const meta = STATUS_META[row.tila] || STATUS_META.puuttuu;
  return row.tila === "voimassa" && row.seuraava ? `${meta.label} (seuraava ${row.seuraava})` : meta.label;
};

// Master canvas is ≈ CONTENT_WIDTH (538.58pt) × 2, matching the scale
// renderCapexYhteenveto.js's own master canvas uses for crispness.
const IMG_WIDTH = 1080;
// No horizontal padding: the original pdfmake table was flush to the
// content edges (paddingLeft/Right: 0), same as every other left-aligned
// element on the page (headings, the green header bar) — a nonzero PAD_X
// here would bake a left inset into the image itself, visibly shifting
// the table right relative to everything else on the page. PAD_Y is just
// cosmetic breathing room above/below and doesn't affect alignment.
const PAD_X = 0;
const PAD_Y = 8;
const USABLE_W = IMG_WIDTH - PAD_X * 2;

// [Tarkastus, Väli, Kohteessa, Viimeisin, Seuraava, Vastuutaho, Tila, Dok.,
// Toimenpiteet / suositukset]. Väli (v) gets the most extra room since its
// long phrases ("5 (koulut, ravintolat, majoitus- ja hoitolaitokset) / 10
// (muut)") drive the tallest rows. Vastuutaho widened again here (88→170)
// — same bug class as before: wrapLines never breaks mid-word, and a real
// Vastuutaho value ("Pelastusviranomainen ja tarvittaessa taloyhtiö") has
// a single ~20-character word that didn't fit 88px's wrap budget at all,
// so it rendered at full width and bled straight into the Tila column
// next to it. Funded from Tarkastus (220→180), which still has slack —
// its name+säädösviite content already wraps comfortably narrower.
const COL_LABELS = ["Tarkastus", "Väli (v)", "Kohteessa", "Viim.", "Seur.", "Vastuutaho", "Tila", "Dok.", "Toimenpiteet / suositukset"];
// Last column absorbs whatever's left of USABLE_W, so the columns always
// sum exactly to it regardless of PAD/IMG_WIDTH tuning.
const COL_WIDTHS_FIXED = [180, 150, 68, 58, 58, 170, 140, 68];
const COL_WIDTHS = [...COL_WIDTHS_FIXED, USABLE_W - COL_WIDTHS_FIXED.reduce((a, b) => a + b, 0)];

const CELL_PAD_X = 8;
const CELL_PAD_Y = 7;
// Short single-word values (years, "Kyllä"/"Ei") never break mid-word, so if
// a column is only just wide enough for its text, that text can butt right
// up against — or bleed past — the next column's text with no visible gap.
// Subtracting this from every column's wrap budget keeps a guaranteed sliver
// of whitespace at the right of each cell regardless of content length.
// Bumped from 6 to 9 alongside the +20% font pass — the old gutter was
// calibrated for the smaller font and had gotten proportionally tighter.
const COL_GUTTER = 9;
const LINE_H = 17; // 14px +20%, to match Rakennuslupataulukko's own +20% pass
const LINE_H_SMALL = 14;

// A full, empty A4 page's content height is ≈745pt (841.89 page height −
// 56.7 top margin − 39.69 bottom margin); 700pt stays safely under that so
// a chunk always eventually fits on some page (its own fresh one, if not
// the current one). No forced pageBreak between chunks — pdfmake already
// auto-flows a sequence of block-level content (including images) onto a
// new page when one doesn't fit the remaining space, same as every other
// image/chart block in this codebase (renderCapexYhteenveto.js included)
// already relies on. Forcing a page break here just wasted the page's
// remaining room instead of letting the next chunk flow into it.
const MAX_CHUNK_H = Math.round(700 * (IMG_WIDTH / CONTENT_WIDTH));

function wrapLines(ctx, text, maxWidth, font) {
  ctx.font = font;
  const words = String(text || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [""];
  const lines = [];
  let line = "";
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(test).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function measureHeader(ctx) {
  const fonts = "bold 14px Lato, sans-serif";
  let maxLines = 1;
  const lines = COL_LABELS.map((label, i) => {
    const l = wrapLines(ctx, label, COL_WIDTHS[i] - CELL_PAD_X * 2 - COL_GUTTER, fonts);
    maxLines = Math.max(maxLines, l.length);
    return l;
  });
  return { lines, height: maxLines * LINE_H + CELL_PAD_Y * 2 };
}

function measureRow(ctx, row) {
  const [wTarkastus, wVali, wSov, wViim, wSeur, wVast, wTila, wDok, wToimen] = COL_WIDTHS;
  const nameLines = wrapLines(ctx, row.nimi || "", wTarkastus - CELL_PAD_X * 2 - COL_GUTTER, "bold 14px Lato, sans-serif");
  const citeLines = row.saadosviite ? wrapLines(ctx, row.saadosviite, wTarkastus - CELL_PAD_X * 2 - COL_GUTTER, "12px Lato, sans-serif") : [];
  const valiLines = wrapLines(ctx, row.vali || "", wVali - CELL_PAD_X * 2 - COL_GUTTER, "14px Lato, sans-serif");
  const sovLines = wrapLines(ctx, SOV_LABELS[row.sovelletaanko] ?? "–", wSov - CELL_PAD_X * 2 - COL_GUTTER, "14px Lato, sans-serif");
  const viimLines = wrapLines(ctx, row.viimeisin || "", wViim - CELL_PAD_X * 2 - COL_GUTTER, "14px Lato, sans-serif");
  const seurLines = wrapLines(ctx, row.seuraava || "", wSeur - CELL_PAD_X * 2 - COL_GUTTER, "14px Lato, sans-serif");
  const vastLines = wrapLines(ctx, row.vastuutaho || "", wVast - CELL_PAD_X * 2 - COL_GUTTER, "14px Lato, sans-serif");
  const tilaLines = wrapLines(ctx, statusText(row), wTila - CELL_PAD_X * 2 - COL_GUTTER, "bold 13px Lato, sans-serif");
  const dokLines = wrapLines(ctx, SOV_LABELS[row.dokumentti] ?? "–", wDok - CELL_PAD_X * 2 - COL_GUTTER, "14px Lato, sans-serif");
  const toimenLines = wrapLines(ctx, row.toimenpiteet || "", wToimen - CELL_PAD_X * 2 - COL_GUTTER, "14px Lato, sans-serif");

  const col0H = nameLines.length * LINE_H + (citeLines.length ? citeLines.length * LINE_H_SMALL + 3 : 0);
  const otherMaxLines = Math.max(
    valiLines.length, sovLines.length, viimLines.length, seurLines.length,
    vastLines.length, tilaLines.length, dokLines.length, toimenLines.length,
  );
  const height = Math.max(34, col0H, otherMaxLines * LINE_H) + CELL_PAD_Y * 2;

  return { height, lines: { nameLines, citeLines, valiLines, sovLines, viimLines, seurLines, vastLines, tilaLines, dokLines, toimenLines } };
}

function drawTableHeader(ctx, x, y, headerPlan) {
  // Same green as the subtotal/footer bars in
  // renderToimenpiteetTekstimuodossa.js's drawSubtotalBar (#04aa00 — the
  // page's own top-banner green, not #198754) — no bottom stroke, matching
  // that bar's own plain-fill styling (same treatment as
  // RenderRakennuslupaTaulukko.js's header).
  ctx.fillStyle = "#04aa00";
  ctx.fillRect(x, y, USABLE_W, headerPlan.height);
  ctx.textAlign = "left";
  ctx.fillStyle = "#ffffff";
  let cx = x;
  headerPlan.lines.forEach((lines, i) => {
    lines.forEach((line, li) => {
      ctx.font = "bold 14px Lato, sans-serif";
      ctx.fillText(line, cx + CELL_PAD_X, y + CELL_PAD_Y + (li + 1) * LINE_H - 3);
    });
    cx += COL_WIDTHS[i];
  });
  return headerPlan.height;
}

function drawGroupTitleBar(ctx, x, y, text, height) {
  // Same grey as the subtotal/footer bars in renderToimenpiteetTekstimuodossa.js's
  // drawSubtotalBar (#f1f3f5 fill, #495057 text) — was a light green
  // (#eafbf1/#198754) before, which read as another header bar rather than
  // a plain row grouping.
  ctx.fillStyle = "#f1f3f5";
  ctx.fillRect(x, y, USABLE_W, height);
  ctx.textAlign = "left";
  ctx.fillStyle = "#495057";
  ctx.font = "bold 14px Lato, sans-serif";
  ctx.fillText(text.toUpperCase(), x + CELL_PAD_X, y + height / 2 + 4);
}

function drawRow(ctx, x, y, row, lines, height) {
  ctx.textAlign = "left";
  let cx = x;

  // Col 0: Tarkastus (name, bold) + säädösviite (muted, smaller) below.
  ctx.fillStyle = "#1c1c1e";
  lines.nameLines.forEach((line, li) => {
    ctx.font = "bold 14px Lato, sans-serif";
    ctx.fillText(line, cx + CELL_PAD_X, y + CELL_PAD_Y + (li + 1) * LINE_H - 3);
  });
  const citeY0 = y + CELL_PAD_Y + lines.nameLines.length * LINE_H + 3;
  ctx.fillStyle = "#8e8e93";
  lines.citeLines.forEach((line, li) => {
    ctx.font = "12px Lato, sans-serif";
    ctx.fillText(line, cx + CELL_PAD_X, citeY0 + (li + 1) * LINE_H_SMALL - 2);
  });
  cx += COL_WIDTHS[0];

  const simpleCol = (colLines, colWidth, font = "14px Lato, sans-serif", color = "#1c1c1e") => {
    ctx.fillStyle = color;
    colLines.forEach((line, li) => {
      ctx.font = font;
      ctx.fillText(line, cx + CELL_PAD_X, y + CELL_PAD_Y + (li + 1) * LINE_H - 3);
    });
    cx += colWidth;
  };

  simpleCol(lines.valiLines, COL_WIDTHS[1]);
  simpleCol(lines.sovLines, COL_WIDTHS[2]);
  simpleCol(lines.viimLines, COL_WIDTHS[3]);
  simpleCol(lines.seurLines, COL_WIDTHS[4]);
  simpleCol(lines.vastLines, COL_WIDTHS[5]);
  simpleCol(lines.tilaLines, COL_WIDTHS[6], "bold 13px Lato, sans-serif", (STATUS_META[row.tila] || STATUS_META.puuttuu).color);
  simpleCol(lines.dokLines, COL_WIDTHS[7]);
  simpleCol(lines.toimenLines, COL_WIDTHS[8]);

  ctx.strokeStyle = "#e5e7eb";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, y + height);
  ctx.lineTo(x + USABLE_W, y + height);
  ctx.stroke();
}

function buildChunkImage(entries, headerPlan) {
  const contentH = entries.reduce((sum, e) => sum + e.height, 0);
  const totalH = PAD_Y * 2 + headerPlan.height + contentH;

  const canvas = document.createElement("canvas");
  canvas.width = IMG_WIDTH;
  canvas.height = totalH;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  let cursorY = PAD_Y;
  cursorY += drawTableHeader(ctx, PAD_X, cursorY, headerPlan);

  for (const entry of entries) {
    if (entry.type === "groupTitle") {
      drawGroupTitleBar(ctx, PAD_X, cursorY, entry.text, entry.height);
    } else {
      drawRow(ctx, PAD_X, cursorY, entry.row, entry.lines, entry.height);
    }
    cursorY += entry.height;
  }

  return canvas.toDataURL("image/png");
}

function drawMaaraaikaistarkastusTaulukko({ data, content, contentWidth = CONTENT_WIDTH }) {
  const { groups = [] } = data || {};
  if (!groups.length) return;

  const measureCanvas = document.createElement("canvas");
  const mctx = measureCanvas.getContext("2d");
  const headerPlan = measureHeader(mctx);
  const GROUP_TITLE_H = 30;

  // Flatten into entries in document order, each already carrying its
  // measured height and (for rows) pre-wrapped lines, so chunking below is
  // just arithmetic and drawing never re-measures text.
  const allEntries = [];
  for (const group of groups) {
    allEntries.push({ type: "groupTitle", text: group.title || "", height: GROUP_TITLE_H, groupTitle: group.title || "" });
    for (const row of group.rows || []) {
      const { height, lines } = measureRow(mctx, row);
      allEntries.push({ type: "row", row, lines, height, groupTitle: group.title || "" });
    }
  }

  // Chunk into page-sized groups of entries. Every chunk repeats the
  // column header (headerPlan.height budget reserved up front); if a
  // chunk boundary falls mid-group, the next chunk opens with a
  // "(jatkuu)" repeat of that group's title so the rows' context isn't lost.
  const chunks = [];
  let current = [];
  let currentH = headerPlan.height;
  let currentGroupTitle = null;

  for (const entry of allEntries) {
    if (entry.type === "groupTitle") currentGroupTitle = entry.text;

    const wouldFit = currentH + entry.height <= MAX_CHUNK_H;
    if (!wouldFit && current.length) {
      chunks.push(current);
      current = [];
      currentH = headerPlan.height;
      if (entry.type === "row" && currentGroupTitle) {
        const cont = { type: "groupTitle", text: `${currentGroupTitle} (jatkuu)`, height: GROUP_TITLE_H };
        current.push(cont);
        currentH += cont.height;
      }
    }
    current.push(entry);
    currentH += entry.height;
  }
  if (current.length) chunks.push(current);

  chunks.forEach((entries, idx) => {
    const imageBase64 = buildChunkImage(entries, headerPlan);
    content.push({
      image: imageBase64,
      width: contentWidth,
      margin: [0, 0, 0, idx === chunks.length - 1 ? bottomMarginItemGlobal : 4],
    });
  });
}

// Fetches the CURRENT version's live data and draws it — this block no
// longer carries its own frozen copy in the report's content (see
// MaaraaikaistarkastusTaulukko.jsx's own comment on why: the report view
// and the Taloyhtiökortti tab must always show the exact same data, not a
// snapshot that can drift from it). A kiinteistö that has never opened this
// table at all — no version exists yet — renders nothing, same as an empty
// `data.groups` did before.
export async function renderMaaraaikaistarkastusTaulukko({ kiinteistoId, content, contentWidth = CONTENT_WIDTH }) {
  if (!kiinteistoId) return;
  const { data: versions } = await getMaaraaikaistarkastusVersiotByKiinteisto(kiinteistoId);
  const current = versions?.[0];
  if (!current) return;
  const { data: tree } = await getMaaraaikaistarkastusVersio(current.id);
  drawMaaraaikaistarkastusTaulukko({ data: { groups: tree.groups }, content, contentWidth });
}
